import React, { useEffect, useState } from 'react';
import { BaseTable, Button, FormField, Label, Modal, Option, Select, TextInput, TextTypes, Toggle } from '@sede-enterprise/shell-ds-react-framework';
import { Search } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components';
import { ColumnDef } from '@tanstack/react-table';
import { Col, Row } from '../flexbox/Flex';
import { AnchorSpan, ContainerDiv } from '../styledComponent/StyledComponent';
import { getData, postData } from '../../services/ApiService';
import { ApiUrls } from '../../helpers/ApiUrls';
import ResponseErrorModal from '../responseErrorModal/ResponseErrorModal';
import Loader from '../loader/Loader';
import { APIErrorResponseInterface } from '../../helpers/InterfacesConstants';
import dayjs from 'dayjs';
import { useToast } from 'rc-toastr';

const MISSING_DAYS_SEARCH_STORAGE_KEY = 'miMissingDaysConfigSearch';

interface MissingDaysConfigItem {
    id: number | null;
    shipToCD: string;
    siteName: string;
    siteIATACD: string;
    countryCD: string;
    countryName: string;
    rowVersion: string;
    missingSince: number;
    dayTransactionMissing: number;
    isActive: boolean;
    createdOn: string;
    createdBy: string;
    modifiedOn: string;
    modifiedBy: string;
}

interface MissingDaysConfigResponseItem extends Omit<MissingDaysConfigItem, 'id'> {
    iD?: number | null;
    id?: number | null;
}

interface MissingDaysConfigFormState {
    id: number | null;
    shipToCD: string;
    rowVersion: string;
    countryName: string;
    siteIATACD: string;
    missingSince: string;
    dayTransactionMissing: number;
    isActive: boolean;
    modifiedBy: string;
    modifiedOn: string;
    createdBy?: string;
    createdOn?: string;
}

const MissingDaysConfig = () => {
    const { toast } = useToast();
    const [missingDaysConfigs, setMissingDaysConfigs] = useState<MissingDaysConfigItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [searchText, setSearchText] = useState(() => sessionStorage.getItem(MISSING_DAYS_SEARCH_STORAGE_KEY) || '');
    const [apiErrorResponse, setApiErrorResponse] = useState<APIErrorResponseInterface | null>();
    const [formState, setFormState] = useState<MissingDaysConfigFormState>({
        id: null,
        shipToCD: '',
        rowVersion: '',
        countryName: '',
        siteIATACD: '',
        missingSince: '',
        dayTransactionMissing: 0,
        isActive: true,
        modifiedBy: '',
        modifiedOn: '',
        createdBy: '',
        createdOn: ''
    });

    const handleEdit = (item: MissingDaysConfigItem) => {
        setFormState({
            id: item.id,
            shipToCD: item.shipToCD,
            rowVersion: item.rowVersion,
            countryName: item.countryName,
            siteIATACD: item.siteIATACD,
            missingSince: String(item.missingSince ?? ''),
            dayTransactionMissing: item.dayTransactionMissing ?? 0,
            isActive: item.isActive,
            modifiedBy: item.modifiedBy,
            modifiedOn: item.modifiedOn,
            createdBy: item.createdBy,
            createdOn: item.createdOn
        });
        setOpenModal(true);
    };

    const closeModal = () => {
        setOpenModal(false);
    };

    const columns: ColumnDef<MissingDaysConfigItem>[] = [
        {
            header: 'Site Code',
            accessorKey: 'shipToCD',
            size: 80
        },
        {
            header: 'Site',
            accessorKey: 'siteIATACD',
            size: 80
        },
        {
            header: 'Country',
            accessorKey: 'countryName',
            size: 100
        },
        {
            header: 'Missing Days',
            accessorKey: 'missingSince',
            size: 80
        },
        {
            header: 'Missing Date',
            accessorKey: 'dayTransactionMissing',
            size: 80
        },
        {
            header: 'Modified By',
            accessorKey: 'modifiedBy',
            size: 120
        },
        {
            header: 'Modified On',
            accessorKey: 'modifiedOn',
            size: 120,
            cell: ({ row }) => row.original.modifiedOn ? dayjs(row.original.modifiedOn).format('DD MMM YYYY') : '-'
        },
        {
            header: 'Is Active',
            accessorKey: 'isActive',
            size: 70,
            cell: ({ row }) => (
                <span
                    style={{
                        color: row.original.isActive ? '#28a745' : '#6c757d',
                        fontWeight: row.original.isActive ? 'bold' : 'normal',
                        fontSize: '14px'
                    }}
                >
                    {row.original.isActive ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            header: 'Action',
            accessorKey: 'id',
            size: 80,
            cell: ({ row }) => (
                <AnchorSpan
                    onClick={() => handleEdit(row.original)}
                    key={row.id}
                    style={{ float: 'right', justifyContent: 'end', minWidth: '35px' }}
                >
                    Edit
                </AnchorSpan>
            )
        }
    ];

    const getMissingDaysConfigs = (searchKeyword = '') => {
        setIsLoading(true);
        const url = `${ApiUrls.miMissingFileConfig}?searchKeyword=${encodeURIComponent(searchKeyword)}`;
        getData(url)
            .then((response) => {
                const data = response.data?.data ?? response.data ?? [];
                const mappedData = (Array.isArray(data) ? data : []).map((item: MissingDaysConfigResponseItem) => ({
                    ...item,
                    id: item.id ?? item.iD ?? null
                }));
                setMissingDaysConfigs(mappedData);
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
                setApiErrorResponse({
                    message: error.response?.data?.message || 'Failed to load Missing Days Config',
                    traceId: error.response?.data?.traceId || ''
                });
            });
    };

    const saveMissingDaysConfig = () => {
        const trimmedMissingSince = formState.missingSince.trim();
        const missingSinceValue = Number(trimmedMissingSince);

        if (trimmedMissingSince === '' || Number.isNaN(missingSinceValue) || missingSinceValue < 0) {
            toast.error('No. of Missing Days is required');
            return;
        }

        setIsSaving(true);

        const requestPayload = {
            id: formState.id,
            shipToCD: formState.shipToCD,
            rowVersion: formState.rowVersion,
            missingSince: missingSinceValue,
            dayTransactionMissing: formState.dayTransactionMissing,
            isActive: formState.isActive
        };

        postData(ApiUrls.addorupdatemissingfileconfig, requestPayload)
            .then((response) => {
                const responseData = response.data?.data ?? response.data;
                if (responseData?.success) {
                    toast.success(responseData.message || 'Missing Days Configuration updated successfully');
                    closeModal();
                    getMissingDaysConfigs(searchText);
                    return;
                }

                toast.error(responseData?.message || 'Failed to update Missing Days Configuration');
            })
            .catch((error) => {
                setApiErrorResponse({
                    message: error.response?.data?.message || 'Failed to update Missing Days Configuration',
                    traceId: error.response?.data?.traceId || ''
                });
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            getMissingDaysConfigs(searchText);
        }
    };

    useEffect(() => {
        getMissingDaysConfigs(searchText);
    }, []);

    return (
        <>
            <ContainerDiv>
                <Row>
                    <Col size="65"><></></Col>
                    <Col size="35">
                        <Row>
                            <Label bold style={{ paddingRight: '4px' }} type={TextTypes.Label} htmlFor="missingDaysSearch">Search</Label>
                            <TextInput
                                size="medium"
                                value={searchText}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    setSearchText(value);
                                    sessionStorage.setItem(MISSING_DAYS_SEARCH_STORAGE_KEY, value);
                                }}
                                suffix={{ node: <Search /> }}
                                placeholder="Search by SiteCode, Site or Country"
                                id="missingDaysSearch"
                                onKeyDown={handleKeyDown}
                            />
                        </Row>
                    </Col>
                </Row>
            </ContainerDiv>
            <ContainerDiv>
                <Row>
                    <Col size="100">
                        {!isLoading && <BaseTable data={missingDaysConfigs} columns={columns} noFooter />}
                        {isLoading && <Loader />}
                    </Col>
                </Row>
            </ContainerDiv>
            <Modal
                open={openModal}
                onClose={closeModal}
                title="Missing Days Configuration"
            >
                <>
                    {isSaving && <Loader />}

                    <FormField id="missingDaysCountry" label="Country">
                        <TextInput id="missingDaysCountry" value={formState.countryName} disabled />
                    </FormField>

                    <FormField id="missingDaysSite" label="Site">
                        <TextInput id="missingDaysSite" value={formState.siteIATACD} disabled />
                    </FormField>

                    <FormField id="missingSince" label="No. of Missing Days (Missing Days)">
                        <TextInput
                            id="missingSince"
                            value={formState.missingSince}
                            onChange={(event) => {
                                const numericValue = event.target.value.replace(/[^0-9]/g, '');
                                setFormState({ ...formState, missingSince: numericValue });
                            }}
                        />
                    </FormField>

                    <FormField id="missingDatesCheck" label="Missing Dates Check (Missing Dates)">
                        <Select
                            id="missingDatesCheck"
                            value={String(formState.dayTransactionMissing)}
                            onChange={(value) => {
                                setFormState({ ...formState, dayTransactionMissing: Number(value) });
                            }}
                        >
                            {Array.from({ length: 8 }, (_, index) => (
                                <Option key={index} value={String(index)}>{index}</Option>
                            ))}
                        </Select>
                    </FormField>

                    <div style={{ marginBottom: '10px' }}>
                        <Row>
                            <Col size="90"><Label bold className="statusLabel">Is Active</Label></Col>
                            <Col size="10"><Toggle checked={formState.isActive} onChange={(value) => { setFormState({ ...formState, isActive: value }); }} /></Col>
                        </Row>
                    </div>

                    <div style={{ marginTop: '24px' }}>
                        <Row>
                            <Col size="60">
                                <div>
                                    <div><strong>Created By:</strong> {formState.createdBy || '-'}</div>
                                    <div><strong>Created On:</strong> {formState.createdOn ? dayjs(formState.createdOn).format('DD MMM YYYY') : '-'}</div>
                                </div>
                            </Col>
                            <Col size="40">
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                    <Button onClick={closeModal} disabled={isSaving}>Cancel</Button>
                                    <Button onClick={saveMissingDaysConfig} disabled={isSaving}>Save</Button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </>
            </Modal>
            <ResponseErrorModal
                errorDetails={apiErrorResponse}
                isOpen={apiErrorResponse ? true : false}
                onModalClose={() => {
                    setApiErrorResponse(null);
                }}
            />
        </>
    );
};

export default MissingDaysConfig;